
/* === parallax.js === */
class ParallaxScroll {
  constructor() {
    this.items = [];
    this.ticking = false;
    this.resizeTimeout = null;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const root = document.documentElement;
    if (root.dataset.parallaxDisableMobile === 'true' && window.innerWidth < 750) return;

    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init(), { once: true });
    }
  }

  init() {
    this.collect();
    if (this.items.length === 0) return;

    this._onScroll = () => {
      if (!this.ticking) {
        this.ticking = true;
        requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      }
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });

    this._onResize = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.refresh(), 200);
    };
    window.addEventListener('resize', this._onResize, { passive: true });

    if (!this._sectionLoad) {
      this._sectionLoad = () => {
        setTimeout(() => this.refresh(), 150);
      };
      document.addEventListener('shopify:section:load', this._sectionLoad);
    }

    this.update();
  }

  collect() {
    const vh = window.innerHeight;

    this.items = [...document.querySelectorAll('[data-parallax]')].map(parent => {
      const intensity = parseFloat(parent.dataset.parallaxIntensity || '0.1');
      const selfMode = parent.hasAttribute('data-parallax-self');

      if (selfMode) {
        parent.style.willChange = 'transform';
        return { parent, target: parent, intensity, extra: 0, selfMode: true };
      }

      const extra = Math.ceil(intensity * vh * 0.6);
      const target = parent.querySelector('img, video, picture');
      if (!target) return null;

      target.style.willChange = 'transform';
      target.style.top = `-${extra}px`;
      target.style.height = `calc(100% + ${extra * 2}px)`;

      return { parent, target, intensity, extra, selfMode: false };
    }).filter(Boolean);
  }

  update() {
    const vh = window.innerHeight;
    const updates = [];

    for (const item of this.items) {
      const el = item.selfMode ? item.target : item.parent;
      const rect = el.getBoundingClientRect();

      if (rect.bottom < -200 || rect.top > vh + 200) continue;

      const center = rect.top + rect.height * 0.5;
      const progress = (center - vh * 0.5) / (vh * 0.5);
      const ty = Math.round(progress * vh * item.intensity);

      updates.push({ target: item.target, ty, selfMode: item.selfMode });
    }

    for (const { target, ty, selfMode } of updates) {
      if (selfMode) {
        target.style.setProperty('--parallax-ty', `${ty}px`);
      } else {
        target.style.transform = `translate3d(0,${ty}px,0)`;
      }
    }
  }

  _resetItem({ target, selfMode }) {
    if (selfMode) {
      target.style.removeProperty('--parallax-ty');
      target.style.willChange = '';
    } else {
      target.style.transform = '';
      target.style.willChange = '';
      target.style.top = '';
      target.style.height = '';
    }
  }

  refresh() {
    for (const item of this.items) this._resetItem(item);
    this.items = [];
    this.collect();
    this.update();
  }

  destroy() {
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
      this._onScroll = null;
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      clearTimeout(this.resizeTimeout);
      this._onResize = null;
    }
    if (this._sectionLoad) {
      document.removeEventListener('shopify:section:load', this._sectionLoad);
      this._sectionLoad = null;
    }
    for (const item of this.items) this._resetItem(item);
    this.items = [];
  }
}

if (!window.ParallaxScroll) {
  window.ParallaxScroll = new ParallaxScroll();
}

